import React, {
    forwardRef,
    useEffect,
    useImperativeHandle,
    useRef,
    useState,
} from 'react';
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    FormHelperText,
    IconButton,
    Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import DescriptionIcon from '@mui/icons-material/Description';
import UploadFileIcon from "@mui/icons-material/UploadFile";

import { ThumbnailBox } from './ThumbnailBox';
import { useHandlePreSign } from '../../hooks/useHandlePreSign';
import { DialogBox } from './DialogBox';
import { FileUploadListView } from './FileUploadListView';

interface FileUploadProps {
    typeId?: string | null | undefined;
    type: 'checklist' | 'queries' | 'tasks' | 'artefact' | 'meeting_manager';
    isHideFiles?: boolean;
    isDisableEdit?: boolean;
    isDisableDelete?: boolean;
    isDisableDownload?: boolean;
    isAllowMultiple?: boolean;
    label?: string;
    filesAllowed?: string[];
    onUploadComplete?: (file_id: string[]) => void;
    onFilesChanged?: (files: any[]) => void;
    onCountChange?: (count: number) => void;
    status?: string;
    isRequired?: boolean;
    /** Render files as a vertical "Files and Attachments" list instead of the thumbnail grid. */
    listView?: boolean;
}

export interface FileUploadRef {
    setId: (id: string | number) => void;
    getId?: () => string | null;
    uploadAllFiles: (givenId?: string | number) => Promise<void>;
    clearFiles: () => void;
 
}

export const FileUpload = forwardRef<FileUploadRef, FileUploadProps>((props, ref) => {

    const {
        typeId,
        type,
        isDisableEdit,
        isDisableDelete,
        isDisableDownload,
        filesAllowed,
        onUploadComplete,
        onFilesChanged,
        onCountChange,
        status,
        isHideFiles = false,
        isAllowMultiple = true,
        isRequired = false,
        listView = false
    } = props;

    const [id, setIdState] = useState<string | number | null>(typeId || null);
    const [requiredError, setRequiredError] = useState(false);

    const { handleUpload, handleFetchFiles, handleDelete, handleDownload } = useHandlePreSign({
        onRefetch: () => {
            id
                ? fetchFiles(id.toString())
                : typeId
                    ? fetchFiles(typeId.toString())
                    : null;
        },
    });

    const fileInputRef = useRef<HTMLInputElement>(null);

    const [loading, setLoading] = useState<boolean>(false);
    const [uploadedFiles, setUploadedFiles] = useState<any[]>([]);
    const [files, setFiles] = useState<any[]>([]);
    const [filesToDelete, setFilesToDelete] = useState<any[]>([]);
    const [notValidFiles, setNotValidFiles] = useState<any[] | null>(null);

    const [prevImage, setPreviewImage] = useState<{
        open: boolean;
        image: any;
    }>({
        open: false,
        image: null,
    });

    useImperativeHandle(ref, () => ({
        setId: (newId: string | number) => {
            if (newId !== undefined && newId !== null) {
                setIdState(newId);
            }
        },
        uploadAllFiles: (givenId?: string | number) =>
            uploadAllFiles(givenId), 
            clearFiles: () => {
            setFiles([]);
            setFilesToDelete([]);
            setNotValidFiles(null);
            // Restore any server files that were optimistically removed via
            // markForDelete but never actually deleted (cancel).
            const currentId = id ?? typeId;
            if (currentId) fetchFiles(currentId.toString());
        }
    }));

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const selectedFiles = Array.from(e.target.files || []);

        let validFiles = selectedFiles;
        let invalidFiles: any[] = [];

        if (filesAllowed) {
            validFiles = selectedFiles.filter((file) => {
                const ext = file.name.split(".").pop()?.toLowerCase();
                const isValid = ext && filesAllowed.includes(ext);
                if (!isValid) invalidFiles.push(file);
                return isValid;
            });

            setNotValidFiles(invalidFiles.length > 0 ? invalidFiles : null);

            if (validFiles.length === 0) return;
        }

        const newFiles = validFiles.map((file) => ({
            file,
            name: file.name,
            type: file.type,
            size: file.size,
            uploading: false,
        }));

        const updatedFiles = isAllowMultiple ? [...files, ...newFiles] : newFiles;
        setFiles(updatedFiles);
        onFilesChanged?.(updatedFiles);
    };

    const uploadAllFiles = async (givenId?: string | number) => {
        const currentId = givenId || id;

        if (isRequired && uploadedFiles.length === 0 && files.length === 0) {
            setRequiredError(true);
            return;
        } else {
            setRequiredError(false);
        }

        if (!currentId) return;

        const updatedFiles = files.map((f) => ({ ...f, uploading: true }));
        setFiles(updatedFiles);

        const uploadedFileIds: any[] = [];
        for (let i = 0; i < updatedFiles.length; i++) {
            const file = updatedFiles[i];
            const response = await handleUpload(type, currentId.toString(), file);
            setFiles((prev) =>
                prev.map((f, index) =>
                    index === i ? { ...f, uploading: false } : f
                )
            );
            if (response && response.data.rows.file_id) {
                uploadedFileIds.push(response.data.rows.file_id);
            }
        }

        for (const deleteFile of filesToDelete) await handleDelete(deleteFile);
        setFilesToDelete([]);

        if (id) {
            fetchFiles(id.toString());
            setFiles([]);
            onUploadComplete && onUploadComplete(uploadedFileIds);
        }
    };

    const fetchFiles = async (id: string) => {
        setLoading(true);
        if (!id) return;
        const response = await handleFetchFiles(id.toString(), type);
        setUploadedFiles(
            response.map((file: any) => ({
                file_id: file.file_id,
                name: file.file_name,
                type: file.mime_type,
                file_uuid: file.file_uuid,
                s3_path: file.s3_path,
                uploading: false,
                thumbnail_s3_path: file.thumbnail_s3_path,
            }))
        );
        setLoading(false);
    };

    const handleDeleteLocal = (file: any) => {
        const updatedFiles = files.filter((value) => value.name !== file.name);
        setFiles(updatedFiles);
        onFilesChanged?.(updatedFiles);
    };

    const markForDelete = (file: any) => {
        if (file.s3_path) {
            setFilesToDelete((prev) => [...prev, file]);
            setUploadedFiles((prev) =>
                prev.filter((f) => f.file_id !== file.file_id)
            );
        } else handleDeleteLocal(file);
    };

    useEffect(() => {
        if (!isHideFiles) {
            setUploadedFiles([]);
            if (id) fetchFiles(id.toString());
            else if (typeId) fetchFiles(typeId);
        }
    }, [typeId]);

    useEffect(() => {
        onCountChange?.(uploadedFiles.length + files.length);
    }, [uploadedFiles.length, files.length]);

    const onPreview = (file: any) => setPreviewImage({ open: true, image: file });

    const openFile = (file: any) =>
        file.s3_path
            ? handleDownload(file)
            : window.open(URL.createObjectURL(file.file), '_blank');

    const isFetching = status === 'listing' && loading;
    const combinedFiles = [...uploadedFiles, ...files];

    if (files.length === 0 && uploadedFiles.length === 0 && isDisableEdit) {
        return (
            <Typography variant="body2" color="text.secondary">
                No documents uploaded
            </Typography>
        )
    }

    return (
        <Box>

            {requiredError && (
                <FormHelperText error>
                    At least one file is required.
                </FormHelperText>
            )}

            {!isDisableEdit && (
                !isAllowMultiple ? (
                    <Box
                        mb={2}
                        display="flex"
                        flexDirection="column"
                        alignItems="center"
                        gap={1}
                    >
                        <Typography variant="body1" color="text.primary">
                            Drop your File here
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Or
                        </Typography>
                        <input
                            type="file"
                            ref={fileInputRef}
                            hidden
                            multiple={false}
                            accept={filesAllowed?.map(ext => `.${ext}`).join(',')}
                            onChange={handleFileChange}
                        />
                        <Button
                            variant="text"
                            startIcon={<UploadFileIcon />}
                            onClick={() => fileInputRef.current?.click()}
                        >
                            Files and Attachments
                        </Button>
                        {filesAllowed && (
                            <FormHelperText>
                                Allowed file types: {filesAllowed.map(f => f.toUpperCase()).join(', ')}
                            </FormHelperText>
                        )}
                        {
                            [...uploadedFiles, ...files].map((file, index) => (
                                <Typography variant="body2" color="text.secondary">
                                    {file.name}
                                </Typography>
                            ))
                        }
                    </Box>
                ) : (
                    <Box mb={2}>
                        <input
                            type="file"
                            ref={fileInputRef}
                            hidden
                            multiple={true}
                            accept={filesAllowed?.map(ext => `.${ext}`).join(',') || 'image/*'}
                            onChange={handleFileChange}
                        />
                        <Button
                            variant="text"
                            startIcon={<UploadFileIcon />}
                            onClick={() => fileInputRef.current?.click()}
                        >
                            Files and Attachments
                        </Button>
                        {filesAllowed && (
                            <FormHelperText>
                                Allowed files: {filesAllowed.join(', ').toUpperCase()}
                            </FormHelperText>
                        )}
                    </Box>
                )
            )}

            {isFetching && (
                <Box display="flex" gap={2} justifyContent="center" alignItems="center">
                    <CircularProgress size={20} />
                    <Typography variant="body2" color="text.secondary">
                        Fetching files...
                    </Typography>
                </Box>
            )}

            {!isFetching && listView && (
                <FileUploadListView
                    files={isHideFiles ? [] : combinedFiles}
                    showHeader={!!isDisableEdit && combinedFiles.length > 0}
                    isDisableDelete={isDisableDelete}
                    isDisableDownload={isDisableDownload}
                    onPreview={onPreview}
                    onOpen={openFile}
                    onDelete={markForDelete}
                />
            )}

            {!isFetching && !listView && (
                <Box display="flex" gap={2} flexWrap="wrap">
                    {status === 'listing' && uploadedFiles.length === 0 && (
                        <Typography variant="subtitle2" color="text.secondary">
                            No documents uploaded
                        </Typography>
                    )}
                    {!isHideFiles && [...uploadedFiles, ...files].map((file, index) => (
                        <Box
                            key={index}
                            position="relative"
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: 120,
                                height: 120,
                                border: '1px solid #ccc',
                                borderRadius: 1,
                                overflow: 'hidden',
                                cursor: 'pointer',
                                '&:hover .hover-actions': { opacity: 1 },
                            }}
                            onClick={() => onPreview(file)}
                        >
                            {file.type?.startsWith('image/') ? (
                                file.s3_path ? (
                                    <ThumbnailBox file={file} />
                                ) : (
                                    <Box
                                        component="img"
                                        src={
                                            file.previewUrl
                                                ? file.previewUrl
                                                : file.file instanceof File
                                                    ? URL.createObjectURL(file.file)
                                                    : undefined
                                        }
                                        alt="Thumbnail"
                                        sx={{ width: '100%', height: '80%', objectFit: 'cover' }}
                                    />
                                )
                            ) : file.type === 'application/pdf' ? (
                                <PictureAsPdfIcon sx={{ fontSize: 60 }} color="error" />
                            ) : (
                                <DescriptionIcon sx={{ fontSize: 60 }} color="action" />
                            )}

                            <Typography
                                variant="caption"
                                noWrap
                                sx={{ width: '80%', textAlign: 'center', mt: 0.5 }}
                            >
                                {file.name}
                            </Typography>

                            {file.uploading && (
                                <Box
                                    sx={{
                                        position: 'absolute',
                                        inset: 0,
                                        bgcolor: 'rgba(255,255,255,0.6)',
                                        display: 'flex',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                        fontSize: 12,
                                        fontWeight: 500,
                                    }}
                                >
                                    Uploading...
                                </Box>
                            )}

                            <Box
                                className="hover-actions"
                                sx={{
                                    position: 'absolute',
                                    inset: 0,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: 1,
                                    bgcolor: 'rgba(0,0,0,0.6)',
                                    opacity: 0,
                                    transition: 'opacity 0.3s',
                                    borderRadius: 1,
                                }}
                            >
                                {!isDisableDelete && (
                                    <IconButton
                                        size="small"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            markForDelete(file);
                                        }}
                                        sx={{ bgcolor: 'white', '&:hover': { bgcolor: 'grey.200' } }}
                                    >
                                        <DeleteIcon fontSize="small" color="error" />
                                    </IconButton>
                                )}
                                {!isDisableDownload && (
                                    <IconButton
                                        size="small"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            openFile(file);
                                        }}
                                        sx={{ bgcolor: 'white', '&:hover': { bgcolor: 'grey.200' } }}
                                    >
                                        <FileDownloadIcon fontSize="small" color="primary" />
                                    </IconButton>
                                )}
                            </Box>
                        </Box>
                    ))}
                </Box>
            )}

            {notValidFiles && notValidFiles.length > 0 && (
                <Box mt={2}>
                    <Alert severity="warning">
                        The following files are not allowed: {notValidFiles.map((f) => f.name).join(', ')}
                    </Alert>
                </Box>
            )}

            <DialogBox
                showModal={prevImage.open}
                onModalClose={() => setPreviewImage({ open: false, image: null })}
                title="Image Preview"
                maxWidth="md"
                subTitle={''}
                actions={[
                    {
                        label: 'Close',
                        handleClick: () => setPreviewImage({ open: false, image: null }),
                        color: 'primary',
                        variant: 'outlined',
                    },
                    {
                        label: 'Download',
                        handleClick: (e: { stopPropagation: () => void }) => {
                            e.stopPropagation();
                            prevImage?.image?.s3_path
                                ? handleDownload(prevImage?.image)
                                : window.open(URL.createObjectURL(prevImage?.image.file), '_blank');
                        },
                        color: 'primary',
                        variant: 'contained',
                    }
                ]}
            >
                <Box>
                    {prevImage.open && (
                        prevImage?.image?.s3_path ? (
                            <ThumbnailBox file={prevImage.image} preView={true} />
                        ) : (
                            <Box
                                sx={{
                                    width: '100%',
                                    height: '80%',
                                    position: 'relative',
                                    backgroundColor: '#f0f0f0',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                }}
                            >
                                <Box
                                    component="img"
                                    src={
                                        prevImage?.image.previewUrl
                                            ? prevImage?.image.previewUrl
                                            : prevImage?.image.file instanceof File
                                                ? URL.createObjectURL(prevImage?.image.file)
                                                : undefined
                                    }
                                    alt="Thumbnail"
                                    sx={{
                                        width: '400px',
                                        height: '400px',
                                        objectFit: 'cover',
                                    }}
                                />
                            </Box>
                        )
                    )}
                </Box>
            </DialogBox>

        </Box>
    );
});